import React from 'react';
import { theme } from '../theme';
import { SealBadge } from './SealBadge';

interface DomainScoreCardProps {
  /** Domain identifier, e.g. "SOV-1" */
  domain: string;
  /** Localized domain label */
  domainLabel: string;
  /** Domain score 0–10 */
  score: number;
  /** SEAL level 0–4 */
  sealLevel: number;
  /** Localized SEAL level label */
  sealLabel: string;
  /** Domain weight percentage (0–1 or 0–100 depending on profile) */
  weight: number;
  /** Optional note, e.g. for zero-weight domains in DE-C3A */
  note?: string;
}

/**
 * Card displaying a single SOV domain's score, SEAL badge, weight, and optional note.
 */
export const DomainScoreCard: React.FC<DomainScoreCardProps> = ({
  domain,
  domainLabel,
  score,
  sealLevel,
  sealLabel,
  weight,
  note,
}) => {
  const barWidth = Math.max(0, Math.min(100, (score / 10) * 100));
  const weightPercent = weight <= 1 ? Math.round(weight * 100) : Math.round(weight);

  return (
    <div
      style={{
        border: `1px solid ${theme.border}`,
        borderRadius: 3,
        padding: 12,
        background: theme.cardBg,
        marginBottom: 8,
      }}
    >
      {/* Header row: domain name + SEAL badge */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
          flexWrap: 'wrap',
          gap: 6,
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 13, color: theme.text }}>
          {domain}: {domainLabel}
        </span>
        <SealBadge level={sealLevel} label={sealLabel} />
      </div>

      {/* Score bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 4,
        }}
      >
        <div
          style={{
            flex: 1,
            height: 8,
            background: '#e5e7eb',
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${barWidth}%`,
              height: '100%',
              background: theme.secondary,
              borderRadius: 4,
              transition: 'width 0.3s ease',
            }}
          />
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: theme.text, minWidth: 36, textAlign: 'right' }}>
          {score.toFixed(1)}/10
        </span>
      </div>

      {/* Weight + note row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: theme.muted }}>
          {weightPercent}% weight
        </span>
        {note && (
          <span
            style={{
              fontSize: 11,
              color: theme.primary,
              fontStyle: 'italic',
            }}
          >
            {note}
          </span>
        )}
      </div>
    </div>
  );
};
